/**
 * RESPONSIBILITY: resolve a @Choice `in` path to the values it currently allows.
 *
 * Dotted segments only, deliberately not JSONPath — a parser nobody here needs. Two
 * origins, because a row cell has two things it could mean:
 *
 *   'categories'    the section root  — the admin-owned vocabulary
 *   'a.b'           nested at the root
 *   './kinds'       the row this cell belongs to
 *
 * The root is the default because that is where a shared vocabulary lives: a field
 * inside `events[]` has to escape its own row to reach `categories` at the top.
 */

const ROW_PREFIX = './';

/** Walk dotted segments. Anything missing on the way answers undefined, never throws. */
function at(source: Record<string, unknown> | undefined, path: string): unknown {
  if (!source) return undefined;
  return path.split('.').filter(Boolean).reduce<unknown>(
    (node, seg) => (node && typeof node === 'object'
      ? (node as Record<string, unknown>)[seg]
      : undefined),
    source,
  );
}

/**
 * The allowed values, or an empty list.
 *
 * Empty is an answer and not a failure: a vocabulary nobody has filled in yet means no
 * value is allowed, which is exactly what an editor should see before they add one.
 */
export function resolveOptions(
  path: string,
  root: Record<string, unknown> | undefined,
  row?: Record<string, unknown>,
): string[] {
  const fromRow = path.startsWith(ROW_PREFIX);
  const found = at(fromRow ? row : root, fromRow ? path.slice(ROW_PREFIX.length) : path);

  return Array.isArray(found)
    ? found.filter((v): v is string => typeof v === 'string')
    : [];
}

/** Every value in `held` that the vocabulary does not contain. Empty means it is legal. */
export function notAllowed(held: unknown, allowed: readonly string[]): string[] {
  const values = Array.isArray(held) ? held : held === undefined || held === '' ? [] : [held];
  return values
    .filter((v) => typeof v === 'string' && !allowed.includes(v))
    .map(String);
}
